export const LAB_TAB = {
  testsScans: "tests-scans",
  packages: "packages",
};

export const ITEM_TYPE = {
  package: "package",
  test: "test",
  scan: "scan",
};

const collectDiagnosticIds = (item, data) => {
  const ids = [];
  if (item.diagnosticId) ids.push(item.diagnosticId._id || item.diagnosticId);
  const list = data?.diagnostics || item.allDiagnostics?.map((d) => d._id) || [];
  list.forEach((id) => {
    const v = id?._id || id;
    if (v && !ids.includes(v)) ids.push(v);
  });
  return ids;
};

export const formatPrice = (amount) => {
  const n = Number(amount) || 0;
  return `₹${n.toLocaleString("en-IN")}`;
};

export const formatParameterLabel = (count) => {
  if (count == null || count === "") return null;
  return `${count} Parameters`;
};

const baseItem = (raw, overrides) => ({
  id: overrides.id,
  title: overrides.title,
  description: overrides.description || "",
  price: Number(overrides.price) || 0,
  originalPrice: overrides.originalPrice != null ? Number(overrides.originalPrice) : null,
  parameterCount: overrides.parameterCount ?? null,
  fastingRequired: Boolean(overrides.fastingRequired),
  homeCollection: Boolean(overrides.homeCollection),
  itemType: overrides.itemType,
  category: overrides.category || null,
  isFeatured: Boolean(overrides.isFeatured),
  primaryAction: { style: overrides.isFeatured ? "filled" : "outlined" },
  diagnosticIds: overrides.diagnosticIds || [],
  _raw: raw,
});

export const mapPackage = (pkg, { isFeatured = false } = {}) =>
  baseItem(pkg, {
    id: pkg.packageId || pkg._id,
    title: pkg.packageName,
    description: pkg.description,
    price: pkg.price,
    originalPrice: pkg.originalPrice || pkg.mrp,
    parameterCount: pkg.totalTestsIncluded,
    fastingRequired: pkg.fastingRequired,
    homeCollection: pkg.homeCollectionAvailable ?? true,
    itemType: ITEM_TYPE.package,
    category: pkg.category,
    isFeatured,
    diagnosticIds: [],
  });

export const mapTest = (item) => {
  const data = item.testId || item;
  const centres = [];
  const seen = new Set();
  (item.allDiagnostics || []).forEach((centre) => {
    const id = centre?._id ? String(centre._id) : "";
    const name = (centre?.name || "").trim();
    if (!name || (id && seen.has(id))) return;
    if (id) seen.add(id);
    centres.push(name);
  });
  const reportHour = item.reportHour ?? data.reportHour;
  const reportIn24Hrs = item.reportIn24Hrs ?? data.reportIn24Hrs;
  const reportLabel = reportHour != null && reportHour !== ""
    ? `${reportHour} hours`
    : reportIn24Hrs
      ? "24 hrs"
      : "";
  const note = [item.description || data.description, item.instruction || data.instruction, item.precaution || data.precaution]
    .map((text) => (text || "").trim())
    .find(Boolean) || "";
  return {
    ...baseItem(item, {
      id: data._id,
      title: (item.testName || data.name || "").trim(),
      description: note,
      price: item.price != null && item.price !== "" ? item.price : data.price,
      parameterCount: (item.subTests || data.subTests || []).length || null,
      fastingRequired: item.fastingRequired ?? data.fastingRequired,
      homeCollection: item.homeCollectionAvailable ?? data.homeCollectionAvailable,
      itemType: ITEM_TYPE.test,
      category: data.category || item.category,
      diagnosticIds: collectDiagnosticIds(item, data),
    }),
    reportLabel,
    centres,
  };
};

export const mapScan = (item) => {
  const data = item.scanId || item;
  return baseItem(item, {
    id: data._id,
    title: data.title || item.title,
    description: data.description || data.preparation || item.description || item.preparation || "",
    price: data.price || item.price,
    parameterCount: null,
    fastingRequired: false,
    homeCollection: data.homeCollectionAvailable ?? item.homeCollectionAvailable ?? false,
    itemType: ITEM_TYPE.scan,
    diagnosticIds: collectDiagnosticIds(item, data),
  });
};

export const pickFeaturedPackage = (packages) => {
  if (!packages.length) return null;
  return packages.reduce(
    (best, p) => ((p.parameterCount || 0) > (best.parameterCount || 0) ? p : best),
    packages[0]
  );
};

export const groupPackagesByCategory = (packages) => {
  const groups = {};
  packages.forEach((p) => {
    const key = p.category || "Other";
    groups[key] = (groups[key] || 0) + 1;
  });
  return Object.entries(groups).map(([name, count]) => ({ name, count }));
};

export const filterCatalog = (items, { search, fastingOnly, homeOnly }) => {
  const q = (search || "").trim().toLowerCase();
  return items.filter((item) => {
    if (fastingOnly && !item.fastingRequired) return false;
    if (homeOnly && !item.homeCollection) return false;
    if (!q) return true;
    return (
      item.title?.toLowerCase().includes(q) ||
      item.description?.toLowerCase().includes(q) ||
      item.category?.toLowerCase().includes(q)
    );
  });
};

export const diagnosticMatchesItems = (diagnostic, itemIds) => {
  if (!itemIds?.length) return true;
  const pools = [
    ...(diagnostic.tests || []),
    ...(diagnostic.scans || []),
    ...(diagnostic.packages || []),
  ];
  return pools.some((x) => itemIds.includes(x._id || x.packageId));
};

export const cartLineFromApi = (item) => ({
  id: item.itemId,
  title: item.title,
  description: item.description || "",
  price: Number(item.totalPayable) || 0,
  fastingRequired: item.fastingRequired,
  homeCollection: item.homeCollectionAvailable,
  itemType: ITEM_TYPE.test,
  _raw: item,
});

export const isMorningSlot = (timeSlot) => {
  const t = String(timeSlot || "");
  if (/am/i.test(t)) return parseInt(t, 10) >= 6;
  if (/pm/i.test(t)) return false;
  return parseInt(t, 10) < 12;
};
